import type { MenuItem } from "@replo/design-system/components/menu/Menu";
import type { ReploComponent } from "schemas/component";
import type { BundleDiscountThreshold } from "schemas/generated/bundleDiscounts";

import * as React from "react";

import Input from "@editor/components/common/designSystem/Input";
import Selectable from "@editor/components/common/designSystem/Selectable";
import useApplyComponentAction from "@editor/hooks/useApplyComponentAction";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import Description from "@replo/design-system/components/label/Description";
import Label from "@replo/design-system/components/label/Label";
import SwitchWithDescription from "@replo/design-system/components/switch/SwitchWithDescription";
import { ToggleGroup } from "@replo/design-system/components/toggle/ToggleGroup";
import twMerge from "@replo/design-system/utils/twMerge";
import { Banknote, Percent, Plus, X } from "lucide-react";

const DEFAULT_BUNDLE_DISCOUNT_THRESHOLDS = [
  { quantity: 3, discount: { type: "percentageOff", value: 25 } },
];

const IntegerInputSelector = ({
  value,
  min,
  onChange,
}: {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  menuItems?: MenuItem[];
}) => {
  return (
    <Input
      min={min}
      type="number"
      value={value}
      onChange={(e) => {
        onChange(Number(e.target.value));
      }}
    />
  );
};

const BasicSettings = ({ component }: { component: ReploComponent }) => {
  const applyComponentAction = useApplyComponentAction();
  const isTiered = Boolean(component.props.isTiered);
  const bundleDiscountThresholds =
    component.props.bundleDiscountThresholds ??
    (DEFAULT_BUNDLE_DISCOUNT_THRESHOLDS as BundleDiscountThreshold[]);
  const handleUpdateDiscountThreshold = (
    thresholdIndex: number,
    updatedThreshold: BundleDiscountThreshold,
  ) => {
    const updatedBundleDiscountThresholds = [
      ...bundleDiscountThresholds.slice(0, thresholdIndex),
      updatedThreshold,
      ...bundleDiscountThresholds.slice(thresholdIndex + 1),
    ];
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleDiscountThresholds: updatedBundleDiscountThresholds,
      },
    });
  };
  const handleAddDiscountThreshold = () => {
    const lastThreshold = bundleDiscountThresholds.slice(-1)[0]!;
    const newThreshold = {
      quantity: lastThreshold.quantity + 1,
      discount: lastThreshold.discount,
    };
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleDiscountThresholds: [...bundleDiscountThresholds, newThreshold],
      },
    });
  };
  const handleRemoveDiscountThreshold = (index: number) => {
    const updatedBundleDiscountThresholds = [
      ...bundleDiscountThresholds.slice(0, index),
      ...bundleDiscountThresholds.slice(index + 1),
    ];
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        bundleDiscountThresholds: updatedBundleDiscountThresholds,
      },
    });
  };
  const toggleIsTiered = () => {
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        isTiered: !isTiered,
      },
    });
  };
  return (
    <div>
      <div className="border-b py-2">
        <Label size="sm" label="Configure Bundle" />
        <Description size="sm" description="Set bundle sizes and rewards" />
      </div>
      <div className="mt-2">
        <div
          className={twMerge(
            "mb-2 grid grid-cols-[45px_45px] gap-3",
            isTiered && "ml-3 grid-cols-[40px_40px]",
          )}
        >
          <Label size="sm" label="Size" />
          <Label size="sm" label="Discount" />
        </div>
        <div className="mb-2 flex flex-col gap-2">
          {bundleDiscountThresholds.map(({ quantity, discount }, index) => {
            const showRemoveButton = bundleDiscountThresholds.length > 1;
            const lastQuantity =
              bundleDiscountThresholds[index - 1]?.quantity ?? 0;
            const tieredSymbol =
              index === bundleDiscountThresholds.length - 1 ? "=" : "≥";
            return (
              <div key={quantity} className="flex items-center gap-1">
                {isTiered && (
                  <span className="text-sm text-placeholder">
                    {tieredSymbol}
                  </span>
                )}
                <IntegerInputSelector
                  min={lastQuantity + 1}
                  value={quantity}
                  onChange={(newQuantity) =>
                    handleUpdateDiscountThreshold(index, {
                      discount,
                      quantity: Number(newQuantity),
                    })
                  }
                />
                <IntegerInputSelector
                  value={discount.value}
                  onChange={(newDiscountValue) =>
                    handleUpdateDiscountThreshold(index, {
                      quantity,
                      discount: {
                        ...discount,
                        value: Number(newDiscountValue),
                      },
                    })
                  }
                />
                <div className="flex-grow">
                  <Selectable
                    layoutClassName="w-full"
                    triggerStartEnhancer={
                      discount.type === "percentageOff" ? (
                        <Percent size={9} />
                      ) : (
                        <Banknote size={9} />
                      )
                    }
                    value={discount.type}
                    onSelect={(
                      newDiscountType: "percentageOff" | "fixedAmount",
                    ) => {
                      handleUpdateDiscountThreshold(index, {
                        quantity,
                        discount: { ...discount, type: newDiscountType },
                      });
                    }}
                    options={[
                      {
                        label: (
                          <div className="flex cursor-pointer items-center gap-1.5">
                            <Percent size={9} /> Percentage
                          </div>
                        ),
                        displayValue: "Percentage",
                        value: "percentageOff",
                      },
                      {
                        label: (
                          <div className="flex cursor-pointer items-center gap-1.5">
                            <Banknote size={9} /> Monetary
                          </div>
                        ),
                        displayValue: "Monetary",
                        value: "fixedAmount",
                      },
                    ]}
                  />
                </div>
                {showRemoveButton && (
                  <IconButton
                    icon={<X size={12} />}
                    onClick={() => handleRemoveDiscountThreshold(index)}
                    variant="tertiary"
                    aria-label="Delete style"
                    size="sm"
                  />
                )}
              </div>
            );
          })}
        </div>
        <Button
          size="sm"
          variant="link"
          onClick={() => handleAddDiscountThreshold()}
          aria-label="Add new text style"
          startEnhancer={<Plus size={14} />}
        >
          Add option
        </Button>
      </div>
      <SwitchWithDescription
        layoutClassName="mt-6"
        isOn={isTiered}
        onChange={() => toggleIsTiered()}
        size="sm"
        label="Tiered rewards"
        tooltipText="Allowing user to receive discounts between thresholds."
      />
    </div>
  );
};

const AdvancedSettings = ({ component }: { component: ReploComponent }) => {
  const applyComponentAction = useApplyComponentAction();
  const { allowMultipleOfSameItem } = component.props;
  const toggleProp = (propName: "allowMultipleOfSameItem") => {
    applyComponentAction({
      componentId: component.id,
      type: "setProps",
      value: {
        [propName]: !component.props[propName],
      },
    });
  };
  return (
    <div className="mt-1.5 flex flex-col gap-1.5">
      <SwitchWithDescription
        isOn={Boolean(allowMultipleOfSameItem)}
        onChange={() => toggleProp("allowMultipleOfSameItem")}
        size="sm"
        variant="card"
        label="Allow multiple of same item"
        description="Users can add more than one of each product/variant"
      />
    </div>
  );
};

const BundleDiscountsCustomPropModifier = ({
  component,
}: {
  component: ReploComponent;
}) => {
  const [configView, setConfigView] = React.useState<"basic" | "advanced">(
    "basic",
  );

  return (
    <div className="p-1.5">
      <ToggleGroup
        size="sm"
        options={[
          { label: "Basic", value: "basic" },
          { label: "Advanced", value: "advanced" },
        ]}
        selectedValue={configView}
        onChange={(value) => {
          setConfigView(value as "basic" | "advanced");
        }}
      />
      {configView === "basic" ? (
        <BasicSettings component={component} />
      ) : (
        <AdvancedSettings component={component} />
      )}
    </div>
  );
};

export default BundleDiscountsCustomPropModifier;
